import type { Store } from "@reduxjs/toolkit";
import { useCallback, useMemo } from "react";
import { useSelector, useStore } from "react-redux";

import { agentClient } from "../agentClient";
import { settleTakeBacks, takeBackThreadFile, useAui } from "../aui";
import { promoteWorkspaceFile } from "../sandbox/client/api";
import { agentActions, workspaceFileKey, type AgentState } from "../store/agentSlice";
import { attachDocument, type ThreadFileInfo } from "./files";
import type { DraggedDocument } from "./documentDrag";
import { useNotice } from "./notice";
import { useThreadId } from "./useThreadId";

type AgentStore = Store<{ agent: AgentState }>;

/** What an add from the Files panel did: put the file on the draft, or found it there already. */
export type WorkspaceAdd = "added" | "already";

/** This tab's adds under way, by thread and file: a second press shares the first, sending nothing. */
const adding = new Map<string, Promise<WorkspaceAdd>>();

/**
 * Shares a Files panel file into the conversation (a thread file), then puts it on the draft as an
 * attached document goes: the next message carries it. A file the draft has from an earlier add
 * stays as it is, sharing nothing. Rejects when the share fails.
 */
export function addWorkspaceFile(
  store: AgentStore,
  threadId: number,
  workspaceId: string,
  path: string
): Promise<WorkspaceAdd> {
  const key = workspaceFileKey(workspaceId, path);
  const busy = `${threadId}:${key}`;
  const running = adding.get(busy);
  if (running) return running;
  const add = shareWorkspaceFile(store, threadId, workspaceId, path, key);
  adding.set(busy, add);
  const settled = () => adding.delete(busy);
  add.then(settled, settled);
  return add;
}

async function shareWorkspaceFile(
  store: AgentStore,
  threadId: number,
  workspaceId: string,
  path: string,
  key: string
): Promise<WorkspaceAdd> {
  const agent = () => store.getState().agent;
  const earlier = agent().workspaceFiles[threadId]?.[key];
  const draft = agent().threads[threadId]?.composer.files ?? [];
  if (earlier != null && draft.some((file) => file.id === earlier)) return "already";
  // Taken off before: its link goes first, so the share makes a new one, never names the old.
  const takeBacks = await settleTakeBacks(threadId);
  let shared;
  try {
    shared = await promoteWorkspaceFile(workspaceId, path, threadId);
    // Taken off while it was shared: the answer named the link that take-back deletes.
    const going = takeBacks.since(shared.id);
    if (going) {
      await going;
      shared = await promoteWorkspaceFile(workspaceId, path, threadId);
    }
  } finally {
    takeBacks.stop();
  }
  store.dispatch(agentActions.workspaceFileAdded({ threadId, key, fileId: shared.id }));
  const file = { id: shared.id, name: shared.name, contentType: shared.content_type };
  // A PDF's pages are counted from this Files panel file (`useCountDraftPdfs`).
  agentClient.attachFile(threadId, file);
  return "added";
}

/** `addWorkspaceFile` on this view's store. */
export function useAddWorkspaceFile() {
  const store = useStore<{ agent: AgentState }>();
  return useCallback(
    (threadId: number, workspaceId: string, path: string) =>
      addWorkspaceFile(store, threadId, workspaceId, path),
    [store]
  );
}

/**
 * The workspace's files on the thread's draft from an add, by path, each with the thread file it is
 * there as: their Add to context buttons show pressed.
 */
export function useWorkspaceFilesInMessage(
  threadId: number | null,
  workspaceId: string | null
): ReadonlyMap<string, number> {
  const added = useSelector((state: { agent: AgentState }) =>
    threadId == null ? undefined : state.agent.workspaceFiles[threadId]
  );
  const draft = useSelector((state: { agent: AgentState }) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.composer.files
  );
  return useMemo(() => {
    const prefix = workspaceId == null ? null : workspaceFileKey(workspaceId, "");
    const onDraft = new Set((draft ?? []).map((file) => file.id));
    const inMessage = new Map<string, number>();
    for (const [key, fileId] of Object.entries(added ?? {})) {
      if (prefix && key.startsWith(prefix) && onDraft.has(fileId)) {
        inMessage.set(key.slice(prefix.length), fileId);
      }
    }
    return inMessage;
  }, [added, draft, workspaceId]);
}

/**
 * A Files panel file's Add to context as a toggle: adds it (`addWorkspaceFile`), or, when it is on
 * the draft as `inMessage`, takes that thread file back off. Rejects when an add fails.
 */
export function useToggleWorkspaceFile() {
  const add = useAddWorkspaceFile();
  return useCallback(
    async (threadId: number, workspaceId: string, path: string, inMessage: number | null) => {
      if (inMessage == null) await add(threadId, workspaceId, path);
      else await takeBackThreadFile(threadId, inMessage);
    },
    [add]
  );
}

/**
 * Adds files to the composer of the runtime around it: dropped or picked files upload; files
 * already on the thread go on the draft as they are (every tab shows them); an application's
 * document is linked by reference first (and given back once on the draft, or null when it could
 * not be), and a Files panel file is shared into the thread first.
 */
export function useAddFiles() {
  const aui = useAui();
  const store = useStore<{ agent: AgentState }>();
  const threadId = useThreadId();
  const { setNotice } = useNotice();
  const addFromFiles = useAddWorkspaceFile();

  const addFiles = useCallback(
    (files: Iterable<File>) => {
      for (const file of files) void aui.composer.addAttachment(file);
    },
    [aui]
  );

  const addThreadFile = useCallback(
    (file: { id: number; name: string; contentType: string }) => {
      if (threadId != null) agentClient.attachFile(threadId, file);
    },
    [threadId]
  );

  const addDocument = useCallback(
    async (document: DraggedDocument): Promise<ThreadFileInfo | null> => {
      if (threadId == null) return null;
      try {
        const file = await attachDocument(threadId, document);
        addThreadFile(file);
        return file;
      } catch {
        setNotice(`Couldn't add ${document.name}`);
        return null;
      }
    },
    [threadId, addThreadFile, setNotice]
  );

  /** Given back as the thread file it is on the draft as (null when it could not be added). */
  const addWorkspace = useCallback(
    async (file: {
      workspaceId: string;
      path: string;
      name: string;
    }): Promise<ThreadFileInfo | null> => {
      if (threadId == null) return null;
      try {
        const added = await addFromFiles(threadId, file.workspaceId, file.path);
        if (added === "already") setNotice(`${file.name} is already on your message.`);
      } catch {
        setNotice(`Couldn't add ${file.name}`);
        return null;
      }
      const { agent } = store.getState();
      const fileId =
        agent.workspaceFiles[threadId]?.[workspaceFileKey(file.workspaceId, file.path)];
      const onDraft = agent.threads[threadId]?.composer.files.find((draft) => draft.id === fileId);
      return onDraft
        ? { id: onDraft.id, name: onDraft.name, contentType: onDraft.contentType }
        : null;
    },
    [threadId, setNotice, addFromFiles, store]
  );

  return { addFiles, addThreadFile, addDocument, addWorkspace };
}
